import { useQuery } from '@tanstack/react-query';
import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  getMonth,
  getWeek,
  getYear,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { schemes } from 'reaviz';
import { fetchAttendanceData } from '../services/attendance.service';
import { AttendanceAPIRecord, AttendanceAPIResponse } from '../types/attendance.types';

// Attendance period type (for API mapping)
export type AttendancePeriodType = 'current_month' | 'last_3_months' | 'last_6_months';

// Interface for grouped data by days
export interface DayAttendanceData {
  date: Date;
  records: {
    userId: number;
    hoursWorked: number;
    status: string;
  }[];
}

// Interface for data grouping by periods
export interface GroupedPeriodData {
  periodStart: Date;
  periodEnd: Date;
  label: string;
  records: {
    userId: number;
    hoursWorked: number;
    status: string;
  }[];
}

// Interface for user series data with explicit color specification
export interface UserSeriesData {
  key: string;
  data: Array<{
    key: Date;
    value: number;
    data: number;
    metadata: {
      periodStart: Date;
      periodEnd: Date;
      label: string;
      userName: string;
      userId: number;
    };
  }>;
  color?: string;
}

// Interface for average values data
export interface AverageSeriesData {
  key: Date;
  value: number;
  data: number;
  metadata: {
    periodStart: Date;
    periodEnd: Date;
    label: string;
  };
}

// Interface for chart data
export interface ChartData {
  userSeries?: UserSeriesData[];
  averageSeries?: AverageSeriesData[];
}

// Interface for data grouped by days
interface DailyGroupedData {
  day: string;
  users: Record<string, number>;
  average: number;
}

// Dictionary of colors for users
const userColors: Record<string, string> = {};

// Function to get a random color
const getRandomColor = (): string => {
  const colors = Object.values(schemes.cybertron) as string[];
  return colors[Math.floor(Math.random() * colors.length)];
};

export interface UseAttendanceDataResult {
  apiData: AttendanceAPIResponse | undefined;
  groupedData: GroupedPeriodData[];
  chartData: UserSeriesData[] | AverageSeriesData[];
  formatXAxisDate: (value: any) => string;
  getPeriodLabel: (date: Date) => string;
  isLoading: boolean;
  error: unknown;
}

interface User {
  id: number;
  name: string;
}

export function useAttendanceData(
  selectedPeriod: AttendancePeriodType,
  selectedUsers: number[],
  showAverageOnly: boolean,
  projectId?: number,
  mockQueryResult?: { data?: AttendanceAPIResponse; isLoading: boolean; error: unknown }
): UseAttendanceDataResult {
  const { t } = useTranslation('dashboard');
  // Get data through react-query or use provided mock data
  const queryResult =
    mockQueryResult ||
    useQuery<AttendanceAPIResponse>({
      queryKey: ['attendance', selectedPeriod, projectId],
      queryFn: () => fetchAttendanceData(selectedPeriod, projectId || 0),
    });

  const { data: apiData, isLoading, error } = queryResult;

  // Group data by dates for basic processing
  const groupedByDayData = useMemo(() => {
    if (!apiData?.records) {
      return [];
    }

    const daysMap = new Map<string, DayAttendanceData>();

    // Process each record
    apiData.records.forEach((record: AttendanceAPIRecord) => {
      const dateStr = record.date.split('T')[0]; // Get only date from ISO string

      if (!daysMap.has(dateStr)) {
        daysMap.set(dateStr, {
          date: parseISO(record.date), // Convert string to Date object
          records: [],
        });
      }

      daysMap.get(dateStr)?.records.push({
        userId: record.userId,
        hoursWorked: record.hoursWorked,
        status: record.status,
      });
    });

    // Convert Map to array and sort by date
    const result = Array.from(daysMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime());

    return result;
  }, [apiData]);

  // Group data by periods (days/weeks/months) depending on selected period
  const groupedData = useMemo(() => {
    if (groupedByDayData.length === 0) {
      return [];
    }

    // For current month, keep grouping by days
    if (selectedPeriod === 'current_month') {
      return groupedByDayData.map((dayData) => ({
        periodStart: dayData.date,
        periodEnd: dayData.date,
        label: format(dayData.date, 'dd MMM', { locale: enUS }),
        records: dayData.records,
      }));
    }

    // For 3 months, group by weeks
    if (selectedPeriod === 'last_3_months') {
      const weekMap = new Map<string, GroupedPeriodData>();

      groupedByDayData.forEach((dayData) => {
        const weekStart = startOfWeek(dayData.date, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(dayData.date, { weekStartsOn: 1 });
        const weekKey = `${getYear(weekStart)}-${getWeek(weekStart, { weekStartsOn: 1 })}`;

        if (!weekMap.has(weekKey)) {
          weekMap.set(weekKey, {
            periodStart: weekStart,
            periodEnd: weekEnd,
            label: `${format(weekStart, 'dd.MM', { locale: enUS })} - ${format(weekEnd, 'dd.MM', { locale: enUS })}`,
            records: [],
          });
        }

        weekMap.get(weekKey)!.records.push(...dayData.records);
      });

      return Array.from(weekMap.values()).sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
    }

    // For 6 months and year, group by months
    if (selectedPeriod === 'last_6_months') {
      const monthMap = new Map<string, GroupedPeriodData>();

      groupedByDayData.forEach((dayData) => {
        const monthStart = startOfMonth(dayData.date);
        const monthEnd = endOfMonth(dayData.date);
        const monthKey = `${getYear(monthStart)}-${getMonth(monthStart)}`;

        if (!monthMap.has(monthKey)) {
          monthMap.set(monthKey, {
            periodStart: monthStart,
            periodEnd: monthEnd,
            label: format(monthStart, 'LLL yyyy', { locale: enUS }),
            records: [],
          });
        }

        monthMap.get(monthKey)!.records.push(...dayData.records);
      });

      return Array.from(monthMap.values()).sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
    }

    // Default return grouping by days
    return groupedByDayData.map((dayData) => ({
      periodStart: dayData.date,
      periodEnd: dayData.date,
      label: format(dayData.date, 'dd MMM', { locale: enUS }),
      records: dayData.records,
    }));
  }, [groupedByDayData, selectedPeriod]);

  // Prepare data for the chart
  const chartData = useMemo(() => {
    if (!apiData) return [];

    // Normalize hours to prevent unrealistic values
    const normalizeHours = (hours: number): number => {
      if (hours <= 0) return 0;
      return Math.min(hours, 24); // we need to fix 418 h per day from API
    };

    // If "average only" mode is selected
    if (showAverageOnly) {
      const averageData = groupedData.map((periodData) => {
        // Filter only records with actual hours worked
        const activeRecords = periodData.records.filter((record) => record.hoursWorked > 0);

        // Calculate total normalized hours
        const totalHours = activeRecords.reduce((sum, record) => sum + normalizeHours(record.hoursWorked), 0);

        // Calculate average only for active records
        const averageHours = activeRecords.length > 0 ? totalHours / activeRecords.length : 0;

        return {
          key: periodData.periodStart,
          value: Number(averageHours.toFixed(1)),
          data: Number(averageHours.toFixed(1)),
          metadata: {
            periodStart: periodData.periodStart,
            periodEnd: periodData.periodEnd,
            label: periodData.label,
          },
        };
      }) as AverageSeriesData[];

      return averageData;
    }

    // If specific users are selected or all users
    const usersToShow =
      selectedUsers.length > 0 ? selectedUsers : apiData?.users?.map((u: User) => u.id) || [];

    // Create data structure grouped by users
    const result = usersToShow.map((userId: number) => {
      const user = apiData?.users?.find((u: User) => u.id === userId);
      const userName = user?.name || t('attendance.userFallback', { id: userId });

      // For each user create array of data points for each period
      const userData = groupedData.map((periodData) => {
        // Filter records only for current user
        const userRecords = periodData.records.filter((r) => r.userId === userId);

        // If no records, return 0
        if (userRecords.length === 0) {
          return {
            key: periodData.periodStart,
            value: 0,
            data: 0,
            metadata: {
              periodStart: periodData.periodStart,
              periodEnd: periodData.periodEnd,
              label: periodData.label,
              userName,
              userId,
            },
          };
        }

        // Get normalized hours for the user in this period
        const totalHours = userRecords.reduce((sum, record) => sum + normalizeHours(record.hoursWorked), 0);

        // For individual users we show total normalized hours
        return {
          key: periodData.periodStart,
          value: Number(totalHours.toFixed(1)),
          data: Number(totalHours.toFixed(1)),
          metadata: {
            periodStart: periodData.periodStart,
            periodEnd: periodData.periodEnd,
            label: periodData.label,
            userName,
            userId,
          },
        };
      });

      // Define possible chart colors
      const CHART_COLORS = Object.values(schemes.cybertron) as string[];

      return {
        key: userName,
        data: userData,
        color: CHART_COLORS[userId % CHART_COLORS.length],
      } as UserSeriesData;
    });

    return result;
  }, [apiData, groupedData, selectedUsers, showAverageOnly]);

  // Format date for X-axis
  const formatXAxisDate = (value: any): string => {
    if (!value || typeof value.getMonth !== 'function') return '';

    // Format the date differently based on selected period
    if (selectedPeriod === 'current_month') {
      return format(value, 'dd', { locale: enUS });
    }

    if (selectedPeriod === 'last_3_months') {
      return t('attendance.weekLabel', { week: getWeek(value, { weekStartsOn: 1 }) });
    }

    if (selectedPeriod === 'last_6_months') {
      return format(value, 'LLL', { locale: enUS });
    }

    return format(value, 'dd.MM', { locale: enUS });
  };

  // Function to get period label based on date
  const getPeriodLabel = (date: Date): string => {
    // If the date is invalid, return empty string
    if (!date || typeof date.getMonth !== 'function') return '';

    // Format differently based on the selected period
    if (selectedPeriod === 'current_month') {
      return format(date, 'EEEE, d MMMM', { locale: enUS });
    }

    if (selectedPeriod === 'last_3_months') {
      const weekStart = startOfWeek(date, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
      return `${format(weekStart, 'PPP', { locale: enUS })} - ${format(weekEnd, 'PPP', { locale: enUS })}`;
    }

    if (selectedPeriod === 'last_6_months') {
      return format(date, 'MMMM yyyy', { locale: enUS });
    }

    return format(date, 'PPP', { locale: enUS });
  };

  return {
    apiData,
    groupedData,
    chartData,
    formatXAxisDate,
    getPeriodLabel,
    isLoading,
    error,
  };
}
